import React, { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { usd, pct, num } from "@/lib/format";
import { Panel, StatusBadge, ProvenanceTag } from "@/components/common/primitives";
import { adaptVariations, buildReviewHistory, OPP_STYLE, OFFER_PERIODS, periodToDays, MAX_HISTORY_DAYS } from "@/lib/analyzerData";
import { DateRangePopover } from "@/components/dashboard/DateRangePopover";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Brush,
} from "recharts";
import {
  Search, Download, Columns3, Maximize2, Minimize2, X, ArrowRight, Bookmark, Trophy,
  CalendarDays, ArrowUp, ArrowDown, ChevronsUpDown, RotateCcw, ChevronLeft, ChevronRight,
} from "lucide-react";

const FULF = { walmart: "Walmart.com", wfs: "WFS", seller: "Seller fulfilled" };
const VAR_COLORS = ["#059669", "#2563EB", "#F97316", "#8B5CF6", "#DB2777", "#0891B2", "#CA8A04"];
const DASH = "—";
const fmtDT = (t) => new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
const fmtDay = (t) => new Date(t).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" });
const fmtHour = (t) => new Date(t).toLocaleTimeString(undefined, { hour: "numeric" });
const fmtRange = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const numCompact = (v) => new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v ?? 0);

// ---------- shared review-history chart helpers ----------
function useInView(ref, rootMargin = "300px") {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView || !ref.current || typeof IntersectionObserver === "undefined") {
      if (typeof IntersectionObserver === "undefined") setInView(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { rootMargin });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, inView, rootMargin]);
  return inView;
}

// Annotate {t, v, change} — change = delta since previous VALID (non-null) observation.
function annotate(points) {
  let prev = null;
  return points.map((p) => {
    const v = p.reviewCount == null ? null : p.reviewCount;
    let change = null;
    if (v != null) { if (prev != null) change = v - prev; prev = v; }
    return { t: p.timestamp, v, change, source: p.source };
  });
}

function ReviewTip({ active, payload, label, variation }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-ctrl border border-edge-subtle bg-surface-0 shadow-float px-2.5 py-2 text-micro" style={{ maxWidth: 250 }}>
      <div className="text-ink-primary font-medium truncate">{variation.title}</div>
      <div className="ident text-ink-muted truncate">{variation.walmartItemId || variation.id}</div>
      <div className="font-mono text-ink-muted mt-1">{fmtDT(label)}</div>
      <div className="tabnum text-ink-primary mt-0.5">Review Count: <b>{p.v == null ? "No observation" : num(p.v)}</b></div>
      {p.change != null && <div className="tabnum text-ink-muted">Change: {p.change >= 0 ? "+" : ""}{num(p.change)}</div>}
      <div className="text-ink-muted mt-0.5">Source: {p.source || "fixture"} · estimated</div>
    </div>
  );
}

const ReviewMiniChart = React.memo(function ReviewMiniChart({ variation, color, days, onOpen }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const data = useMemo(() => annotate(buildReviewHistory(variation, days)), [variation, days]);
  const has = data.some((d) => d.v != null);
  const hourly = days <= 1;
  return (
    <button ref={ref} data-testid={`var-reviewhist-${variation.id}`} onClick={onOpen}
      title={`${variation.title} review history — open full graph`} aria-label={`${variation.title} review history — open full graph`}
      className="block rounded-ctrl border border-edge-subtle bg-surface-0 hover:ring-1 hover:ring-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
      style={{ width: 220, height: 88 }}>
      {!has ? (
        <span className="grid place-items-center h-full text-micro text-ink-muted px-2 text-center">No review history available</span>
      ) : !inView ? (
        <span className="grid place-items-center h-full text-micro text-ink-muted">…</span>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" vertical={false} />
            <XAxis dataKey="t" tickFormatter={hourly ? fmtHour : fmtDay} tick={{ fontSize: 8, fill: "#8A94A6" }} minTickGap={26} height={14} tickLine={false} />
            <YAxis width={38} tick={{ fontSize: 8, fill: "#8A94A6" }} tickFormatter={numCompact} tickLine={false} axisLine={false} domain={["auto", "auto"]} />
            <RTooltip content={(p) => <ReviewTip {...p} variation={variation} />} />
            <Line type="step" dataKey="v" stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </button>
  );
});

// Full Reviews History graph — unrestricted period controls (presets + custom range + prev/next + reset zoom).
function ReviewHistoryModal({ variation, color, initialDays, onClose }) {
  const [days, setDays] = useState(initialDays || 365);
  const [offset, setOffset] = useState(0); // days ago the window ENDS (prev/next shifts this)
  const [rangeLabel, setRangeLabel] = useState(null);
  const [zoomKey, setZoomKey] = useState(0);
  const data = useMemo(() => annotate(buildReviewHistory(variation, days, offset)), [variation, days, offset]);
  const has = data.some((d) => d.v != null);
  const start = new Date(Date.now() - (days + offset) * 864e5);
  const end = new Date(Date.now() - offset * 864e5);
  const hourly = days <= 1;
  const applyPreset = (p) => { setDays(periodToDays(p) === 1825 ? MAX_HISTORY_DAYS : periodToDays(p)); setOffset(0); setRangeLabel(null); };
  const applyCustom = (r) => {
    const len = Math.max(1, Math.round((r.end - r.start) / 864e5));
    const endAgo = Math.max(0, Math.round((Date.now() - r.end) / 864e5));
    setDays(len); setOffset(endAgo); setRangeLabel(r.label);
  };
  return createPortal(
    <div className="fixed inset-0 z-[130] bg-black/45 grid place-items-center p-4" onClick={onClose} data-testid="var-review-modal">
      <div role="dialog" aria-modal="true" aria-label={`Reviews history for ${variation.title}`} onClick={(e) => e.stopPropagation()}
        className="bg-surface-0 border border-edge-subtle rounded-panel shadow-float w-full ez-rise-in flex flex-col" style={{ maxWidth: "min(94vw, 1200px)", maxHeight: "90vh" }}>
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-edge-subtle">
          <div className="min-w-0">
            <div className="text-stitle text-ink-primary truncate">Reviews History · {variation.title}</div>
            <div className="ident text-micro text-ink-muted">{variation.walmartItemId || variation.id}</div>
          </div>
          <button data-testid="var-review-modal-close" autoFocus onClick={onClose} aria-label="Close reviews history" className="h-8 w-8 grid place-items-center text-ink-muted hover:bg-surface-2 rounded-ctrl shrink-0"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-4 py-2.5 border-b border-edge-subtle flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro">
            {OFFER_PERIODS.map((p) => {
              const active = !rangeLabel && offset === 0 && (periodToDays(p) === 1825 ? days === MAX_HISTORY_DAYS : days === periodToDays(p));
              return <button key={p} data-testid={`var-modal-period-${p}`} onClick={() => applyPreset(p)} className={cn("px-2 h-7 font-mono", active ? "ez-seg-active" : "ez-seg")}>{p}</button>;
            })}
          </div>
          <DateRangePopover value={rangeLabel ? { label: rangeLabel } : null} allStart={new Date(Date.now() - MAX_HISTORY_DAYS * 864e5)} maxDate={new Date()} align="start" onApply={applyCustom}
            trigger={<button data-testid="var-modal-custom-range" className={cn("inline-flex items-center gap-1.5 h-7 px-2.5 rounded-ctrl border text-micro font-mono whitespace-nowrap ez-press", rangeLabel ? "ez-seg-active" : "border-edge-subtle hover:bg-surface-2")}><CalendarDays className="h-3.5 w-3.5" />{rangeLabel || "Custom range"}</button>} />
          <div className="inline-flex items-center gap-1">
            <button data-testid="var-modal-prev" onClick={() => { setOffset((o) => o + days); setRangeLabel(null); }} className="inline-flex items-center gap-1 h-7 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2"><ChevronLeft className="h-3.5 w-3.5" />Prev</button>
            <button data-testid="var-modal-next" disabled={offset <= 0} onClick={() => { setOffset((o) => Math.max(0, o - days)); setRangeLabel(null); }} className={cn("inline-flex items-center gap-1 h-7 px-2 rounded-ctrl border border-edge-subtle text-micro", offset <= 0 ? "text-ink-muted cursor-not-allowed" : "hover:bg-surface-2")}>Next<ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
          <button data-testid="var-modal-reset-zoom" onClick={() => setZoomKey((k) => k + 1)} className="inline-flex items-center gap-1 h-7 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2"><RotateCcw className="h-3.5 w-3.5" />Reset zoom</button>
          <span data-testid="var-modal-range" className="ml-auto text-micro font-mono text-ink-muted whitespace-nowrap">{fmtRange(start)} – {fmtRange(end)}</span>
        </div>

        <div className="p-4 overflow-auto ez-scroll">
          {!has ? (
            <div className="h-[360px] grid place-items-center text-compact text-ink-muted">No review history available for this period.</div>
          ) : (
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer key={zoomKey} width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 14, bottom: 6, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" vertical={false} />
                  <XAxis dataKey="t" tickFormatter={hourly ? fmtHour : fmtDay} minTickGap={56} tick={{ fontSize: 11, fill: "#8A94A6" }} />
                  <YAxis width={56} tick={{ fontSize: 11, fill: "#8A94A6" }} tickFormatter={num} label={{ value: "Review count", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#8A94A6" } }} />
                  <RTooltip content={(p) => <ReviewTip {...p} variation={variation} />} />
                  <Line type="step" dataKey="v" name="Review count" stroke={color} strokeWidth={2.2} dot={false} isAnimationActive={false} connectNulls={false} />
                  <Brush dataKey="t" height={18} travellerWidth={8} tickFormatter={fmtDay} stroke="#059669" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-micro text-ink-muted mt-3">Review-count observations over time (step = discrete changes). Missing observations are shown as gaps and never interpolated. Fixture-derived / estimated.</p>
        </div>
      </div>
    </div>, document.body);
}

// ---------- column model ----------
const COLS = [
  { id: "walmartItemId", label: "Walmart Item ID", ident: true, get: (r) => r.walmartItemId, sort: (r) => r.walmartItemId },
  { id: "upc", label: "UPC / GTIN", ident: true, get: (r) => r.upc, sort: (r) => r.upc },
  { id: "packCount", label: "Pack", num: true, get: (r) => r.packCount, sort: (r) => r.packCount },
  { id: "featuredOffer", label: "Featured Offer", num: true, get: (r) => usd(r.pricing.featuredOffer), sort: (r) => r.pricing.featuredOffer },
  { id: "unitPrice", label: "Unit price", num: true, get: (r) => (r.pricing.unitPrice == null ? DASH : usd(r.pricing.unitPrice)), sort: (r) => r.pricing.unitPrice },
  { id: "walmartRetail", label: "Walmart.com", num: true, get: (r) => usd(r.pricing.walmartRetail), sort: (r) => r.pricing.walmartRetail },
  { id: "lowestMarketplace", label: "Lowest marketplace", num: true, get: (r) => usd(r.pricing.lowestMarketplace), sort: (r) => r.pricing.lowestMarketplace },
  { id: "average90d", label: "90-day avg", num: true, get: (r) => usd(r.pricing.average90d), sort: (r) => r.pricing.average90d },
  { id: "featuredSeller", label: "Featured seller", get: (r) => r.competition.featuredSeller, sort: (r) => r.competition.featuredSeller },
  { id: "fulfillment", label: "Fulfillment", get: (r) => FULF[r.competition.featuredFulfillment] || DASH, sort: (r) => r.competition.featuredFulfillment },
  { id: "wfsEligible", label: "WFS eligible", get: (r) => (r.competition.wfsEligible ? "Yes" : "No"), sort: (r) => (r.competition.wfsEligible ? 1 : 0) },
  { id: "sellerCount", label: "Sellers", num: true, get: (r) => r.competition.sellerCount, sort: (r) => r.competition.sellerCount },
  { id: "demand", label: "Est. 30d demand", num: true, get: (r) => (r.demand.estimated30DayUnits == null ? DASH : num(r.demand.estimated30DayUnits)), sort: (r) => r.demand.estimated30DayUnits },
  { id: "stock", label: "Stock", get: (r) => r.demand.stockStatus, sort: (r) => r.demand.stockStatus },
  { id: "rating", label: "Rating", num: true, get: (r) => r.reviews.rating, sort: (r) => r.reviews.rating },
  // Reviews + Reviews History kept ADJACENT (review COUNT — distinct from Rating).
  {
    id: "reviews", label: "Reviews", num: true, sortable: true, sort: (r) => r.reviewCount,
    render: (r, ctx) => {
      if (r.reviewCount == null) return <div className="text-right text-ink-muted" title="Review count unavailable"><div>{DASH}</div><div className="text-micro">No reviews data</div></div>;
      const share = ctx.shareMap[r.id];
      const value = ctx.compact ? numCompact(r.reviewCount) : num(r.reviewCount);
      return (
        <div className="text-right">
          <div className="flex items-center justify-end gap-1">
            {ctx.isHighest(r) && <Trophy data-testid={`var-review-trophy-${r.id}`} aria-label="Highest review count among variations" className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
            <span data-testid={`var-review-${r.id}`} className="text-base font-semibold tabnum text-ink-primary">{value}</span>
          </div>
          <div data-testid={`var-review-share-${r.id}`} className="text-micro text-ink-muted tabnum">{share == null ? DASH : `${share.toFixed(2)}%`}</div>
        </div>
      );
    },
  },
  {
    id: "reviewsHistory", label: "Reviews History", noSort: true,
    render: (r, ctx) => <ReviewMiniChart variation={r} color={ctx.colorFor(r.id)} days={ctx.days} onOpen={() => ctx.openFull(r)} />,
  },
  { id: "netProfit", label: "Net profit", num: true, get: (r) => usd(r.profitability.netProfit), sort: (r) => r.profitability.netProfit },
  { id: "roi", label: "ROI", num: true, get: (r) => pct(r.profitability.roi), sort: (r) => r.profitability.roi, roi: true },
  { id: "margin", label: "Margin", num: true, get: (r) => pct(r.profitability.margin), sort: (r) => r.profitability.margin },
  { id: "maxAcq", label: "Max acquisition", num: true, get: (r) => usd(r.profitability.maximumAcquisitionCost), sort: (r) => r.profitability.maximumAcquisitionCost },
  { id: "matchConfidence", label: "Match conf.", num: true, get: (r) => (r.matchConfidence == null ? DASH : `${r.matchConfidence}%`), sort: (r) => r.matchConfidence },
  { id: "trackingSince", label: "Tracking since", ident: true, get: (r) => r.trackingSince, sort: (r) => r.trackingSince },
];

// Reviews + Reviews History appear in EVERY preset (adjacent + visible by default).
const VARIATION_PRESETS = {
  Default: ["featuredOffer", "unitPrice", "sellerCount", "reviews", "reviewsHistory", "rating", "roi"],
  History: ["featuredOffer", "reviews", "reviewsHistory", "rating", "trackingSince"],
  Performance: ["featuredOffer", "sellerCount", "demand", "reviews", "reviewsHistory", "netProfit", "roi", "margin"],
  "All Columns": COLS.map((c) => c.id),
};

const COL_KEY = "ez_variation_cols";
const SORT_KEY = "ez_variation_sort";
const presetHidden = (name) => { const on = new Set(VARIATION_PRESETS[name]); const n = {}; COLS.forEach((c) => { n[c.id] = !on.has(c.id); }); return n; };
const loadHidden = () => { try { const s = localStorage.getItem(COL_KEY); if (s) return JSON.parse(s); } catch { /* ignore */ } return presetHidden("Default"); };
const loadSort = () => { try { return JSON.parse(localStorage.getItem(SORT_KEY)) || { key: "reviews", dir: "desc" }; } catch { return { key: "reviews", dir: "desc" }; } };

function OpportunityCell({ row }) {
  const s = OPP_STYLE[row.opportunity] || OPP_STYLE.unknown;
  return (
    <span className="inline-flex items-center gap-1.5" title={row.reasons.join(" · ") || "No scoring reasons"}>
      <StatusBadge tone={s.tone}>{row.opportunityScore == null ? DASH : `${row.opportunityScore} · ${s.label}`}</StatusBadge>
    </span>
  );
}

function SortHeader({ label, dir, onClick, testid }) {
  const Icon = dir === "desc" ? ArrowDown : dir === "asc" ? ArrowUp : ChevronsUpDown;
  return (
    <button data-testid={testid} onClick={onClick} aria-label={`Sort by ${label}`} title={`Sort by ${label}`}
      className={cn("inline-flex items-center gap-1 hover:text-ink-primary", dir && "text-emerald-600")}>
      {label}<Icon className="h-3 w-3" />
    </button>
  );
}

function VariationGrid({ rows, hidden, selected, onSelect, ctx, sort, onToggleReviewSort }) {
  const visibleCols = COLS.filter((c) => !hidden[c.id]);
  return (
    <div className="overflow-auto ez-scroll border-t border-edge-subtle" style={{ maxHeight: 560 }} data-testid="variation-grid">
      <table className="text-compact border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr className="text-micro text-ink-muted text-left">
            <th className="sticky left-0 top-0 z-30 bg-surface-2 px-2 py-2 border-b border-r border-edge-subtle w-9"> </th>
            <th className="sticky left-9 top-0 z-30 bg-surface-2 px-3 py-2 border-b border-r border-edge-subtle min-w-[220px]">Product &amp; attributes</th>
            {visibleCols.map((c) => (
              <th key={c.id} className={cn("sticky top-0 z-20 bg-surface-2 px-3 py-2 border-b border-edge-subtle whitespace-nowrap", c.num && "text-right")}>
                {c.id === "reviews"
                  ? <SortHeader label="Reviews" testid="var-reviews-sort" dir={sort.key === "reviews" ? sort.dir : null} onClick={onToggleReviewSort} />
                  : c.label}
              </th>
            ))}
            <th className="sticky top-0 z-20 bg-surface-2 px-3 py-2 border-b border-edge-subtle">Opportunity</th>
            <th className="sticky right-0 top-0 z-30 bg-surface-2 px-3 py-2 border-b border-l border-edge-subtle text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={visibleCols.length + 4} className="px-4 py-12 text-center text-ink-muted">No variations match your filters.</td></tr>}
          {rows.map((r) => {
            const isSel = selected.includes(r.id);
            return (
              <tr key={r.id} className={cn("hover:bg-surface-1", r.isBest && "bg-emerald-100/30")}>
                <td className={cn("sticky left-0 z-10 bg-surface-0 px-2 py-2 border-b border-r border-edge-subtle", r.isBest && "border-l-2 border-l-emerald-500")}>
                  <input type="checkbox" aria-label={`Select ${r.title}`} data-testid={`var-select-${r.id}`} checked={isSel} onChange={() => onSelect(r.id)} />
                </td>
                <td className="sticky left-9 z-10 bg-surface-0 px-3 py-2 border-b border-r border-edge-subtle min-w-[220px]">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded bg-surface-2 shrink-0" aria-hidden />
                    <div className="min-w-0">
                      <div className="text-ink-primary truncate" title={r.title}>{r.title}</div>
                      <div className="text-micro text-ink-muted truncate">{Object.entries(r.attributes).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                    </div>
                  </div>
                </td>
                {visibleCols.map((c) => (
                  <td key={c.id} className={cn("px-3 py-2 border-b border-edge-subtle whitespace-nowrap align-middle", c.num && !c.render && "text-right tabnum", c.ident && "ident text-micro",
                    c.roi && (r.profitability.roi ?? 0) >= 30 ? "text-emerald-600" : c.roi ? "text-amber-600" : "")}>
                    {c.render ? c.render(r, ctx) : (c.get(r) ?? DASH)}
                  </td>
                ))}
                <td className="px-3 py-2 border-b border-edge-subtle"><OpportunityCell row={r} /></td>
                <td className="sticky right-0 z-10 bg-surface-0 px-3 py-2 border-b border-l border-edge-subtle text-right">
                  <button data-testid={`var-load-${r.id}`} className="inline-flex items-center gap-1 text-micro text-signal-blue hover:underline whitespace-nowrap" onClick={() => onSelect.load?.(r.walmartItemId)}>Load<ArrowRight className="h-3 w-3" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CompareDrawer({ rows, onClose }) {
  const FIELDS = [
    ["Featured Offer", (r) => r.pricing.featuredOffer, "money", "high"],
    ["90-day avg", (r) => r.pricing.average90d, "money", "low"],
    ["Unit price", (r) => r.pricing.unitPrice, "money", "low"],
    ["Sellers", (r) => r.competition.sellerCount, "int", "low"],
    ["Est. 30d demand", (r) => r.demand.estimated30DayUnits, "int", "high"],
    ["Review count", (r) => r.reviewCount, "int", "high"],
    ["Rating", (r) => r.reviews.rating, "num", "high"],
    ["Net profit", (r) => r.profitability.netProfit, "money", "high"],
    ["ROI", (r) => r.profitability.roi, "pct", "high"],
    ["Margin", (r) => r.profitability.margin, "pct", "high"],
    ["Max acquisition", (r) => r.profitability.maximumAcquisitionCost, "money", "high"],
    ["Opportunity", (r) => r.opportunityScore, "int", "high"],
    ["Match confidence", (r) => r.matchConfidence, "int", "high"],
  ];
  const fmt = (v, t) => (v == null ? DASH : t === "money" ? usd(v) : t === "pct" ? pct(v) : num(v));
  return createPortal(
    <div className="fixed inset-0 z-[110] bg-black/40 flex" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Compare variations" onClick={(e) => e.stopPropagation()} data-testid="variation-compare"
        className="ml-auto h-full w-full max-w-3xl bg-surface-0 border-l border-edge-subtle flex flex-col ez-rise-in">
        <div className="h-14 flex items-center justify-between px-4 border-b border-edge-subtle">
          <span className="text-stitle text-ink-primary">Compare {rows.length} variations</span>
          <button data-testid="compare-close" autoFocus onClick={onClose} aria-label="Close compare" className="h-8 w-8 grid place-items-center text-ink-muted hover:bg-surface-2 rounded-ctrl"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-auto ez-scroll p-4">
          <table className="w-full text-compact">
            <thead><tr><th className="text-left px-3 py-2 text-micro text-ink-muted">Metric</th>
              {rows.map((r) => <th key={r.id} className="px-3 py-2 text-left text-ink-primary min-w-[150px]">{r.title}{r.isBest && <StatusBadge tone="success" className="ml-1.5">Best</StatusBadge>}</th>)}
            </tr></thead>
            <tbody>
              {FIELDS.map(([label, get, t, dir]) => {
                const vals = rows.map(get);
                const nums = vals.filter((v) => typeof v === "number");
                const best = nums.length ? (dir === "high" ? Math.max(...nums) : Math.min(...nums)) : null;
                return (
                  <tr key={label} className="border-t border-edge-subtle">
                    <td className="px-3 py-2 text-ink-secondary">{label}</td>
                    {rows.map((r) => { const v = get(r); return <td key={r.id} className={cn("px-3 py-2 tabnum", v === best && best != null ? "text-emerald-600 font-semibold" : "text-ink-primary")}>{fmt(v, t)}</td>; })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-micro text-ink-muted mt-3">Green marks the strongest value per row (fewer sellers is treated as better). Fixture-derived.</p>
        </div>
      </div>
    </div>, document.body);
}

export function WalmartVariationsTab({ bundle, onLoadVariation, onSaveLead, onAddToPurchaseList }) {
  const all = useMemo(() => adaptVariations(bundle?.variations || []), [bundle]);
  const [filters, setFilters] = useState({ q: "", profitable: false, inStock: false, wfs: false, excludeRetail: false });
  const [sort, setSort] = useState(loadSort);
  const [selected, setSelected] = useState([]);
  const [hidden, setHidden] = useState(loadHidden);
  const [colsOpen, setColsOpen] = useState(false);
  const [compare, setCompare] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [period, setPeriod] = useState("1Y");
  const [customRange, setCustomRange] = useState(null);
  const [fullVar, setFullVar] = useState(null); // variation open in Reviews History modal
  const rootRef = useRef(null);

  const days = customRange ? Math.max(1, Math.round((customRange.end - customRange.start) / 864e5)) : (periodToDays(period) === 1825 ? MAX_HISTORY_DAYS : periodToDays(period));

  useEffect(() => { try { localStorage.setItem(SORT_KEY, JSON.stringify(sort)); } catch { /* ignore */ } }, [sort]);

  const filtered = useMemo(() => all.filter((v) =>
    (v.title || "").toLowerCase().includes(filters.q.toLowerCase()) &&
    (!filters.profitable || (v.profitability.roi ?? 0) >= 30) &&
    (!filters.inStock || v.demand.stockStatus === "in-stock") &&
    (!filters.wfs || v.competition.wfsEligible) &&
    (!filters.excludeRetail || !v.competition.walmartRetailPresent)), [all, filters]);

  const rows = useMemo(() => {
    const cmp = {
      opportunity: (a, b) => (b.opportunityScore ?? -1) - (a.opportunityScore ?? -1),
      roi: (a, b) => (b.profitability.roi ?? -1) - (a.profitability.roi ?? -1),
      profit: (a, b) => (b.profitability.netProfit ?? -1) - (a.profitability.netProfit ?? -1),
      price: (a, b) => (a.pricing.featuredOffer ?? 0) - (b.pricing.featuredOffer ?? 0),
    };
    if (sort.key === "reviews") {
      // Numeric, raw count. 0 is valid. null/undefined sort LAST. Stable (Array.sort is stable).
      return [...filtered].sort((a, b) => {
        const av = a.reviewCount, bv = b.reviewCount;
        const am = av == null, bm = bv == null;
        if (am && bm) return 0;
        if (am) return 1;
        if (bm) return -1;
        return sort.dir === "asc" ? av - bv : bv - av;
      });
    }
    return [...filtered].sort(cmp[sort.key] || cmp.opportunity);
  }, [filtered, sort]);

  // Review share computed across the CURRENTLY VISIBLE (filtered) variation set. 0 is valid; null/undefined excluded.
  const { shareMap, maxCount } = useMemo(() => {
    const valid = rows.filter((r) => typeof r.reviewCount === "number");
    const total = valid.reduce((s, r) => s + r.reviewCount, 0);
    const map = {};
    valid.forEach((r) => { map[r.id] = total > 0 ? (r.reviewCount / total) * 100 : 0; });
    return { shareMap: map, maxCount: valid.length ? Math.max(...valid.map((r) => r.reviewCount)) : null };
  }, [rows]);

  const colorFor = (id) => VAR_COLORS[all.findIndex((v) => v.id === id) % VAR_COLORS.length];
  const ctx = {
    shareMap, compact, days, colorFor,
    isHighest: (r) => typeof r.reviewCount === "number" && maxCount != null && r.reviewCount === maxCount,
    openFull: (r) => setFullVar(r),
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleReviewSort = () => setSort((s) => (s.key === "reviews" ? { key: "reviews", dir: s.dir === "desc" ? "asc" : "desc" } : { key: "reviews", dir: "desc" }));
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 4 ? s : [...s, id]));
  toggleSelect.load = onLoadVariation;
  const toggleCol = (id) => setHidden((h) => { const n = { ...h, [id]: !h[id] }; try { localStorage.setItem(COL_KEY, JSON.stringify(n)); } catch { /* ignore */ } return n; });
  const applyPreset = (name) => {
    const on = new Set(VARIATION_PRESETS[name]);
    const n = {}; COLS.forEach((c) => { n[c.id] = !on.has(c.id); });
    setHidden(n); try { localStorage.setItem(COL_KEY, JSON.stringify(n)); } catch { /* ignore */ }
  };
  const exportCsv = () => {
    const cols = ["walmartItemId", "upc", "packCount", "featuredOffer", "sellerCount", "reviewCount", "reviewShare", "rating", "netProfit", "roi", "margin", "opportunityScore"];
    const csv = [cols.join(","), ...rows.map((r) => [r.walmartItemId, r.upc, r.packCount, r.pricing.featuredOffer, r.competition.sellerCount,
      r.reviewCount ?? "", shareMap[r.id] != null ? shareMap[r.id].toFixed(2) : "", r.reviews.rating,
      r.profitability.netProfit, r.profitability.roi, r.profitability.margin, r.opportunityScore].join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "ezisell-variations.csv"; a.click(); URL.revokeObjectURL(a.href);
  };
  const selectedRows = all.filter((r) => selected.includes(r.id));
  const chip = "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl text-micro ez-press whitespace-nowrap";

  const body = (
    <section ref={rootRef} data-testid="walmart-variations-tab" className="space-y-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-stitle text-ink-primary">Variation Opportunities</h2>
          <p className="text-compact text-ink-secondary">Compare Walmart variants by reviews, price, demand, competition, and profit.</p>
        </div>
        <div className="flex items-center gap-2 text-micro text-ink-muted"><ProvenanceTag kind="fixture" /><span>{all.length} variations tracked</span></div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 h-8 px-2 rounded-ctrl border border-edge-subtle">
          <Search className="h-3.5 w-3.5 text-ink-muted" />
          <input data-testid="var-search" value={filters.q} onChange={(e) => setF("q", e.target.value)} placeholder="Search attributes" className="bg-transparent text-compact focus:outline-none w-32" />
        </div>
        {[["profitable", "Only profitable"], ["inStock", "In stock"], ["wfs", "WFS eligible"], ["excludeRetail", "Exclude Walmart retail"]].map(([k, l]) => (
          <button key={k} data-testid={`var-filter-${k}`} onClick={() => setF(k, !filters[k])} className={cn(chip, filters[k] ? "ez-seg-active" : "ez-seg ez-seg-outline")}>{l}</button>
        ))}
        <select data-testid="var-sort" value={`${sort.key}:${sort.dir}`} onChange={(e) => { const [key, dir] = e.target.value.split(":"); setSort({ key, dir }); }}
          className="h-8 px-2 rounded-ctrl border border-edge-subtle text-compact bg-surface-0">
          <option value="reviews:desc">Sort: Reviews (high→low)</option>
          <option value="reviews:asc">Sort: Reviews (low→high)</option>
          <option value="opportunity:desc">Sort: Opportunity</option>
          <option value="roi:desc">Sort: ROI</option>
          <option value="profit:desc">Sort: Net profit</option>
          <option value="price:asc">Sort: Price</option>
        </select>
        <button data-testid="var-compact-toggle" onClick={() => setCompact((c) => !c)} className={cn(chip, compact ? "ez-seg-active" : "ez-seg ez-seg-outline")}>Compact numbers</button>
        <div className="ml-auto flex items-center gap-2">
          {/* Reviews-history period control (drives every row's Reviews History mini-chart) */}
          <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll text-micro" role="group" aria-label="Reviews history period">
            {OFFER_PERIODS.map((p) => (
              <button key={p} data-testid={`var-period-${p}`} onClick={() => { setCustomRange(null); setPeriod(p); }} className={cn("px-2 h-8 font-mono", !customRange && period === p ? "ez-seg-active" : "ez-seg")}>{p}</button>
            ))}
          </div>
          <DateRangePopover value={customRange} allStart={new Date(Date.now() - MAX_HISTORY_DAYS * 864e5)} maxDate={new Date()} align="end" onApply={(r) => setCustomRange(r)}
            trigger={<button data-testid="var-custom-range" className={cn(chip, customRange ? "ez-seg-active" : "border-edge-subtle hover:bg-surface-2")}><CalendarDays className="h-3.5 w-3.5" />{customRange ? customRange.label : "Custom range"}</button>} />
          {customRange && <button data-testid="var-custom-clear" onClick={() => setCustomRange(null)} className={cn(chip, "border-edge-subtle hover:bg-surface-2")}>Clear</button>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="var-compare" disabled={selected.length < 2} onClick={() => setCompare(true)} className={cn(chip, selected.length >= 2 ? "border-signal-blue text-signal-blue hover:bg-signal-blue/10" : "border-edge-subtle text-ink-muted cursor-not-allowed")}>Compare {selected.length ? `(${selected.length})` : ""}</button>
        <button data-testid="var-add-purchase" disabled={!selected.length} onClick={() => onAddToPurchaseList(selected)} className={cn(chip, selected.length ? "border-edge-subtle hover:bg-surface-2" : "border-edge-subtle text-ink-muted cursor-not-allowed")}><Bookmark className="h-3.5 w-3.5" />Purchase list</button>
        <div className="relative">
          <button data-testid="var-columns" onClick={() => setColsOpen((o) => !o)} className={cn(chip, "border-edge-subtle hover:bg-surface-2")}><Columns3 className="h-3.5 w-3.5" />Columns</button>
          {colsOpen && (
            <div className="absolute right-0 mt-1 z-40 w-60 max-h-80 overflow-auto ez-scroll bg-surface-0 border border-edge-subtle rounded-panel shadow-menu p-2" data-testid="var-columns-menu">
              <div className="flex flex-wrap gap-1 mb-2">
                {Object.keys(VARIATION_PRESETS).map((p) => (
                  <button key={p} data-testid={`var-preset-${p}`} onClick={() => applyPreset(p)} className="h-6 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-emerald-50 hover:text-emerald-700">{p}</button>
                ))}
              </div>
              {COLS.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 text-compact hover:bg-surface-2 rounded-ctrl cursor-pointer">
                  <input type="checkbox" data-testid={`var-col-${c.id}`} checked={!hidden[c.id]} onChange={() => toggleCol(c.id)} />{c.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <button data-testid="var-export" onClick={exportCsv} className={cn(chip, "border-edge-subtle hover:bg-surface-2")}><Download className="h-3.5 w-3.5" />CSV</button>
        <button data-testid="var-fullscreen" onClick={() => setFullscreen((f) => !f)} className={cn(chip, "border-edge-subtle hover:bg-surface-2")}>{fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}</button>
      </div>

      <Panel className="p-0 overflow-hidden">
        {!bundle ? <div className="p-12 text-center text-ink-muted" data-testid="var-loading">Loading variations…</div>
          : <VariationGrid rows={rows} hidden={hidden} selected={selected} onSelect={toggleSelect} ctx={ctx} sort={sort} onToggleReviewSort={toggleReviewSort} />}
      </Panel>
      <p className="text-micro text-ink-muted">Review share is calculated across the {rows.filter((r) => typeof r.reviewCount === "number").length} currently visible variations (recalculated when filters change). 🏆 marks the highest review count. Reviews (count) is kept separate from Rating (avg stars). Opportunity score weights profit, demand, competition, stability, Featured Offer accessibility, match confidence and freshness. Fixture-derived; missing factors are skipped.</p>
      {compare && <CompareDrawer rows={selectedRows} onClose={() => setCompare(false)} />}
      {fullVar && <ReviewHistoryModal variation={fullVar} color={colorFor(fullVar.id)} initialDays={days} onClose={() => setFullVar(null)} />}
    </section>
  );
  return fullscreen
    ? createPortal(<div className="fixed inset-0 z-[100] bg-surface-1 overflow-auto ez-scroll p-4 sm:p-6">{body}</div>, document.body)
    : body;
}

export default WalmartVariationsTab;
